import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export type InfoType = "route" | "truck" | "job" | "pickup" | "depot";

export function useInfoPanel() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();

  const infoType = params.get("infoType") as InfoType | null;
  const infoId = params.get("infoId");
  const isOpen = Boolean(infoType && infoId);

  const open = useCallback(
    // `fromCard`: opened from a link inside the panel, so the panel offers a way back to it.
    (type: InfoType, id: string | number, { fromCard = false } = {}) => {
      const sp = new URLSearchParams(params);
      sp.set("infoType", type);
      sp.set("infoId", String(id));
      navigate(
        { search: sp.toString() },
        { replace: false, state: fromCard ? { infoBack: true } : null },
      );
    },
    [navigate, params],
  );

  const close = useCallback(() => {
    const sp = new URLSearchParams(params);
    sp.delete("infoType");
    sp.delete("infoId");
    navigate({ search: sp.toString() }, { replace: false });
  }, [navigate, params]);

  const canGoBack = isOpen && (location.state as { infoBack?: boolean } | null)?.infoBack === true;
  const back = useCallback(() => navigate(-1), [navigate]);

  return useMemo(
    () => ({ infoType, infoId, isOpen, open, close, canGoBack, back }),
    [infoType, infoId, isOpen, open, close, canGoBack, back],
  );
}
